/**
 * Encryption Service for DM Encryption at Rest
 *
 * Singleton service that manages the master encryption key lifecycle and provides
 * encrypt/decrypt operations for DM messages. The master key is fetched from a
 * Cloud Function at initialization time and held in memory only.
 *
 * Architecture:
 * - Master key stored in Firebase Functions Secrets (Google Secret Manager)
 * - Cloud Function `getEncryptionKey` provides key to authenticated clients
 * - Per-conversation keys derived via HKDF(masterKey, conversationId)
 * - AES-256-GCM envelope encryption for all sensitive message fields
 * - Dual-read mode: unencrypted (legacy) messages pass through unchanged
 *
 * Usage:
 *   import { encryptionService } from '@/services/encryption';
 *
 *   // Initialize (called by EncryptionContext on auth)
 *   await encryptionService.initialize();
 *
 *   // Encrypt a message
 *   const encrypted = await encryptionService.encryptMessage(conversationId, envelope);
 *
 *   // Decrypt a message
 *   const envelope = await encryptionService.decryptMessage(conversationId, encrypted);
 */

import { importMasterKey, deriveConversationKey, encryptPayload, decryptPayload } from './crypto';
import { GET_ENCRYPTION_KEY_FUNCTION } from './constants';
import type {
  EncryptedData,
  EncryptedEnvelope,
  EncryptionState,
  GetEncryptionKeyResponse,
} from './types';

// Re-export types and constants for convenient imports
export type { EncryptedData, EncryptedEnvelope, EncryptionState } from './types';
export {
  ENCRYPTED_PLACEHOLDER,
  DECRYPT_FAILED_PLACEHOLDER,
  DECRYPTING_PLACEHOLDER,
} from './constants';

// ==================== Lazy-loaded Firebase Functions ====================

let functionsModule: typeof import('firebase/functions') | null = null;
let functionsInstance: import('firebase/functions').Functions | null = null;

/**
 * Lazily load Firebase Functions module and instance.
 * Follows the same pattern as admin.ts and moderation.ts.
 */
async function getFirebaseFunctions(): Promise<{
  httpsCallable: typeof import('firebase/functions').httpsCallable;
  functions: import('firebase/functions').Functions;
}> {
  if (!functionsModule || !functionsInstance) {
    const [mod, { app }] = await Promise.all([
      import('firebase/functions'),
      import('@/config/firebase'),
    ]);
    functionsModule = mod;
    functionsInstance = mod.getFunctions(app);
  }
  return {
    httpsCallable: functionsModule.httpsCallable,
    functions: functionsInstance,
  };
}

// ==================== EncryptionService ====================

class EncryptionService {
  private masterKey: CryptoKey | null = null;
  private keyVersion: number = 0;
  private keyCache: Map<string, CryptoKey> = new Map();
  private initPromise: Promise<void> | null = null;
  private _error: string | null = null;

  /**
   * Initialize the encryption service by fetching the master key
   * from the Cloud Function. Safe to call multiple times -- subsequent
   * calls return the same promise.
   */
  async initialize(): Promise<void> {
    if (this.masterKey) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      this._error = null;

      const { httpsCallable, functions } = await getFirebaseFunctions();
      const callable = httpsCallable<void, GetEncryptionKeyResponse>(
        functions,
        GET_ENCRYPTION_KEY_FUNCTION
      );

      const result = await callable();
      const { keyMaterial, version } = result.data;

      // Validate key format
      if (!keyMaterial || !/^[0-9a-f]{64}$/i.test(keyMaterial)) {
        throw new Error('Invalid encryption key format received from server');
      }

      this.masterKey = await importMasterKey(keyMaterial);
      this.keyVersion = version;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to initialize encryption';

      // Check for specific Cloud Function error codes
      const errorCode = (err as { code?: string })?.code;
      if (errorCode === 'functions/failed-precondition') {
        this._error = 'Encryption key not configured. Contact your administrator.';
      } else if (errorCode === 'functions/unauthenticated') {
        this._error = 'Authentication required for encryption.';
      } else {
        this._error = message;
      }

      console.error('[EncryptionService] Initialization failed:', this._error);
      // Reset so a future initialize() call can retry
      this.initPromise = null;
      throw new Error(this._error);
    }
  }

  /** Whether the service is ready to encrypt/decrypt */
  isReady(): boolean {
    return this.masterKey !== null;
  }

  /** Current state for UI consumption */
  getState(): EncryptionState {
    return {
      ready: this.isReady(),
      error: this._error,
      keyVersion: this.keyVersion,
    };
  }

  // ==================== Per-Conversation Key Derivation ====================

  /**
   * Get or derive the AES-256-GCM key for a specific conversation.
   * Derived keys are cached in memory for the session duration.
   */
  private async getConversationKey(conversationId: string): Promise<CryptoKey> {
    if (!this.masterKey) {
      throw new Error('Encryption service not initialized');
    }

    const cached = this.keyCache.get(conversationId);
    if (cached) return cached;

    const derived = await deriveConversationKey(this.masterKey, conversationId);
    this.keyCache.set(conversationId, derived);
    return derived;
  }

  // ==================== Message Encryption ====================

  /**
   * Encrypt a message envelope (text, attachments, quoted message) into
   * a single AES-256-GCM ciphertext blob.
   */
  async encryptMessage(
    conversationId: string,
    envelope: EncryptedEnvelope
  ): Promise<EncryptedData> {
    const key = await this.getConversationKey(conversationId);
    const plaintext = JSON.stringify(envelope);
    const { ciphertext, iv } = await encryptPayload(key, plaintext);

    return {
      ciphertext,
      iv,
      keyVersion: this.keyVersion,
    };
  }

  /**
   * Decrypt a message ciphertext back into the original envelope.
   */
  async decryptMessage(conversationId: string, data: EncryptedData): Promise<EncryptedEnvelope> {
    const key = await this.getConversationKey(conversationId);
    const plaintext = await decryptPayload(key, data.ciphertext, data.iv);
    return JSON.parse(plaintext) as EncryptedEnvelope;
  }

  // ==================== Preview Encryption ====================

  /**
   * Encrypt a short preview string (for conversation list display).
   */
  async encryptPreview(conversationId: string, text: string): Promise<EncryptedData> {
    const key = await this.getConversationKey(conversationId);
    const { ciphertext, iv } = await encryptPayload(key, text);

    return {
      ciphertext,
      iv,
      keyVersion: this.keyVersion,
    };
  }

  /**
   * Decrypt a preview string.
   */
  async decryptPreview(conversationId: string, data: EncryptedData): Promise<string> {
    const key = await this.getConversationKey(conversationId);
    return decryptPayload(key, data.ciphertext, data.iv);
  }

  // ==================== Cleanup ====================

  /**
   * Clear all sensitive key material from memory.
   * Called on logout to ensure keys don't persist.
   */
  destroy(): void {
    this.masterKey = null;
    this.keyVersion = 0;
    this.keyCache.clear();
    this.initPromise = null;
    this._error = null;
  }
}

/** Singleton instance used across the application */
export const encryptionService = new EncryptionService();
